import React from "react";
import { cn } from "@athena/ui";
import type { OrganizationActivityStatsQuery } from "@/graphql/userAction.generated";
import type { OrganizationLmsStatsQuery } from "@/graphql/assignments.generated";
import type { OrganizationMembershipStatsQuery } from "@/graphql/organizations.generated";
import type { TimeFrame } from "@/components/TimeFrameSelector";
import {
    clientPlatforms,
    dailyActions,
    engagementCategoryLabelMap,
    formatMonthLabel,
    frequencyTierLabelMap,
    pct,
    platformLabel,
    rosterTotal,
    topVideoTags,
} from "../statsHelpers";
import { REPORT_WIDTH_PX } from "./exportReportPdf";
import { balancedRows } from "./reportLayout";
import {
    isSectionIncluded,
    selectedItems,
    type ReportItem,
    type ReportItemId,
    type ReportSectionId,
    type ReportSelection,
} from "./reportSections";
import {
    hasText,
    isAudienceWritten,
    isEventWritten,
    isTestimonialWritten,
    type ReportBrand,
    type ReportEvent,
    type ReportNarrative,
} from "./reportContent";

type Stats = OrganizationActivityStatsQuery["organizationActivityStats"];
type LmsStats = OrganizationLmsStatsQuery["organizationLmsStats"];
type MembershipStats = OrganizationMembershipStatsQuery["organizationMembershipStats"];

export interface PartnerReportProps {
    brand: ReportBrand;
    organizationName: string;
    timeFrame: TimeFrame;
    narrative: ReportNarrative;
    selection: ReportSelection;
    stats: Stats;
    lmsStats: LmsStats | undefined;
    membershipStats: MembershipStats | undefined;
    includeTagViews: boolean;
    includeTopicViews: boolean;
}

/**
 * The template's four coaching tiles, lightest to darkest, each keeping its color whichever are
 * chosen. Every figure and label holds at least 4.5:1 against its tile.
 */
const COACHING_TILES: Partial<Record<ReportItemId, { tile: string; figure: string; label: string }>> = {
    "coaching-users": { tile: "bg-report-cream", figure: "text-report-navy", label: "text-report-navy" },
    "coaching-messages": { tile: "bg-report-teal", figure: "text-report-navy", label: "text-report-navy" },
    "messages-to-coach": { tile: "bg-report-teal-deep", figure: "text-text-white", label: "text-text-white" },
    "coaching-sessions": { tile: "bg-report-navy", figure: "text-report-teal", label: "text-text-white" },
};

/** Bars in a column chart; past this many, upright figures would run into each other, so they turn sideways. */
const MAX_UPRIGHT_FIGURES = 14;

const count = (value: number) => value.toLocaleString("en-US");

const metricValue = (id: ReportItemId, stats: Stats, lmsStats: LmsStats | undefined): string => {
    const total = rosterTotal(stats);
    switch (id) {
        case "total-users":
            return count(total);
        case "active-users":
            return count(stats.activeUsers);
        case "active-user-rate":
            return total ? pct(stats.activeUsers / total) : "—";
        case "total-actions":
            return count(stats.totalActions);
        case "avg-actions-per-user":
            return stats.activeUsers ? (stats.totalActions / stats.activeUsers).toFixed(1) : "—";
        case "email-reach-rate":
            return pct(stats.emailReachRate);
        case "email-click-rate":
            return pct(stats.emailClickRate);
        case "coaching-users":
            return count(stats.coachingUsers);
        case "coaching-sessions":
            return count(stats.coachingSessions);
        case "coaching-messages":
            return count(stats.coachingMessages);
        case "messages-to-coach":
            return count(stats.messagesToCoach);
        case "users-in-progress":
            return lmsStats ? count(lmsStats.usersWithActiveAssignments) : "—";
        case "users-started-course":
            return lmsStats ? count(lmsStats.usersWhoStartedCourses) : "—";
        case "users-completed-course":
            return lmsStats ? count(lmsStats.usersWhoCompletedCourses) : "—";
    }
};

/** One unit the PDF keeps on a single page. */
const Block: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <div data-report-block="" className={cn("px-18 pt-7", className)}>
        {children}
    </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 className="font-condensed text-report-navy border-report-teal mb-5 border-b-3 pb-1.5 text-xl font-bold tracking-wide uppercase">
        {children}
    </h2>
);

const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <h3
        className={cn("font-condensed text-report-slate mb-3 text-lg font-semibold tracking-wide uppercase", className)}
    >
        {children}
    </h3>
);

/**
 * Cards in rows of at most `perRow`, each stretching across its row — whichever cards are chosen, a
 * section fills the page's width instead of leaving empty columns. `gap` spaces both ways.
 */
const CardRows: React.FC<{ perRow: number; gap: string; className?: string; children: React.ReactNode }> = ({
    perRow,
    gap,
    className,
    children,
}) => (
    <div className={cn("flex flex-col", gap, className)}>
        {balancedRows(React.Children.toArray(children), perRow).map((row, index) => (
            <div key={index} className={cn("flex *:min-w-0 *:flex-1", gap)}>
                {row}
            </div>
        ))}
    </div>
);

const FigureCard: React.FC<{ item: ReportItem; value: string }> = ({ item, value }) => (
    <div className="bg-report-cream rounded-3xl px-5 py-4">
        <p className="font-condensed text-report-teal-deep text-5xl leading-tight font-semibold">{value}</p>
        <p className="text-report-navy mt-1 font-semibold">{item.label}</p>
        <p className="text-report-slate text-sm">{item.caption}</p>
    </div>
);

const MeterRows: React.FC<{ rows: { label: string; value: number }[]; unit: string }> = ({ rows, unit }) => {
    const max = Math.max(...rows.map((row) => row.value), 1);
    return (
        <div className="flex flex-col gap-3">
            {rows.map((row) => (
                <div key={row.label} className="flex items-center gap-4">
                    <span className="text-report-navy w-40 shrink-0 text-sm font-semibold">{row.label}</span>
                    <span className="bg-report-stone h-3.5 flex-1 overflow-hidden rounded-full">
                        <span
                            className="bg-report-teal-deep block h-full rounded-full"
                            style={{ width: `${(row.value / max) * 100}%` }}
                        />
                    </span>
                    <span className="text-report-slate w-28 shrink-0 text-right text-sm italic">
                        {count(row.value)} {unit}
                    </span>
                </div>
            ))}
        </div>
    );
};

const Legend: React.FC<{ entries: { label: string; fill: string }[] }> = ({ entries }) => (
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1">
        {entries.map((entry) => (
            <span key={entry.label} className="text-report-navy inline-flex items-center gap-2 text-sm">
                <span className={cn("inline-block h-3 w-3", entry.fill)} />
                {entry.label}
            </span>
        ))}
    </div>
);

interface ColumnSeries {
    key: string;
    label: string;
    fill: string;
}

/**
 * Every bar with a value carries its figure — a printed chart has no hover to read a value from. An
 * empty bar has nothing to label: a row of zeros would only crowd out the figures that matter. The
 * figure sits just above its bar, outside the bar's height, so the bars stay in proportion.
 */
const ColumnChart: React.FC<{ rows: { label: string; values: Record<string, number> }[]; series: ColumnSeries[] }> = ({
    rows,
    series,
}) => {
    const max = Math.max(...rows.flatMap((row) => series.map((s) => row.values[s.key] ?? 0)), 1);
    const sideways = rows.length * series.length > MAX_UPRIGHT_FIGURES;
    const gap = sideways ? "gap-1" : "gap-2";
    return (
        <div>
            {/* The top padding is the room the tallest bar's figure needs above it. */}
            <div
                className={cn(
                    "border-report-stone flex items-end border-b",
                    gap,
                    sideways ? "h-56 pt-12" : "h-48 pt-6",
                )}
            >
                {rows.map((row) => (
                    <div key={row.label} className="flex h-full flex-1 items-end justify-center gap-1">
                        {series.map((s) => {
                            const value = row.values[s.key] ?? 0;
                            return (
                                <div
                                    key={s.key}
                                    className={cn("relative max-w-12 flex-1 rounded-t-sm", s.fill)}
                                    style={{ height: `${(value / max) * 100}%` }}
                                >
                                    {value > 0 && (
                                        <span
                                            className={cn(
                                                "text-report-navy absolute bottom-full left-1/2 mb-1 text-xs font-semibold whitespace-nowrap",
                                                // Turned about its bottom-left corner, the figure would sit a
                                                // line-height left of centre; ml-2 is half that line.
                                                sideways ? "ml-2 origin-bottom-left -rotate-90" : "-translate-x-1/2",
                                            )}
                                        >
                                            {count(value)}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className={cn("mt-1.5 flex", gap)}>
                {rows.map((row) => (
                    <span key={row.label} className="text-report-slate flex-1 text-center text-xs">
                        {row.label}
                    </span>
                ))}
            </div>
            {series.length > 1 && <Legend entries={series.map((s) => ({ label: s.label, fill: s.fill }))} />}
        </div>
    );
};

const RankedList: React.FC<{ rows: { key: string; label: string; views?: number }[] }> = ({ rows }) => (
    <ol className="flex flex-col gap-2.5">
        {rows.map((row, index) => (
            <li key={row.key} className="text-report-navy flex gap-3">
                <span className="text-report-teal-deep font-semibold">{index + 1}</span>
                <span>
                    {row.label}
                    {row.views !== undefined && (
                        <span className="text-report-slate text-sm"> · {count(row.views)} views</span>
                    )}
                </span>
            </li>
        ))}
    </ol>
);

const EventCard: React.FC<{ title: string; detailLabel: string; event: ReportEvent }> = ({
    title,
    detailLabel,
    event,
}) => {
    const lines = [
        { label: "Event / session name", value: event.name },
        { label: "Date", value: event.date },
        { label: "Attendees", value: event.attendees },
        { label: detailLabel, value: event.detail },
        { label: "Notes", value: event.notes },
    ].filter((line) => hasText(line.value));
    return (
        <div className="bg-report-cream px-4 py-3.5">
            <CardTitle className="text-report-teal-deep mb-1.5 text-base">{title}</CardTitle>
            {lines.map((line) => (
                <p key={line.label} className="text-report-navy text-sm">
                    <span className="font-semibold">{line.label}:</span> <span className="italic">{line.value}</span>
                </p>
            ))}
        </div>
    );
};

/**
 * The partner success report as it prints: the template's banner, headings and cards, filled with
 * the sections and sub-sections chosen on the stats page and the text written in Report content.
 *
 * Laid out at a fixed letter width and only ever mounted off-screen for the capture, so its fixed
 * report palette is unaffected by the admin's light or dark theme. Every `Block` is a unit the PDF
 * keeps whole on a page.
 */
export const PartnerReport = React.forwardRef<HTMLDivElement, PartnerReportProps>(
    (
        {
            brand,
            organizationName,
            timeFrame,
            narrative,
            selection,
            stats,
            lmsStats,
            membershipStats,
            includeTagViews,
            includeTopicViews,
        },
        ref,
    ) => {
        const included = (id: ReportSectionId) => isSectionIncluded(id, selection);
        const hasVideos = stats.videoViewSummaries.length > 0;

        const reachItems = selectedItems("users-actions", selection);
        const coachingItems = selectedItems("coaching", selection);
        const emailItems = selectedItems("email-newsletter", selection);
        const lmsItems = lmsStats ? selectedItems("lms", selection) : [];

        const showTiers = included("frequency-tiers");
        const showTopics = hasVideos && included("top-video-topics");
        const showTags = hasVideos && included("top-video-tags");
        const showPlatformActions = included("platforms-actions");
        const showPlatformUsers = included("platforms-users");
        const showDaily = timeFrame.mode === "MONTH" && included("daily-actions");

        const events = [
            { title: "Virtual", detailLabel: "Format", event: narrative.virtualEvent },
            { title: "In-person", detailLabel: "Location / chapter", event: narrative.inPersonEvent },
        ].filter((card) => isEventWritten(card.event));
        const audiences = narrative.audiences.filter(isAudienceWritten);

        return (
            <div
                ref={ref}
                className="bg-report-paper text-report-navy pb-10 font-sans"
                style={{ width: REPORT_WIDTH_PX }}
            >
                <div data-report-block="" className="bg-report-navy flex items-start justify-between px-18 py-9">
                    <div>
                        <img src={brand.logo} alt={brand.label} className="h-14 max-w-64 object-contain object-left" />
                        <p className="font-condensed text-report-teal mt-2 text-lg font-semibold tracking-wide uppercase">
                            Partner success report
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-text-white text-2xl font-bold">{organizationName}</p>
                        <p className="text-report-cream mt-1 text-sm">{timeFrame.label}</p>
                        {hasText(narrative.preparedBy) && (
                            <p className="text-report-cream text-sm">Prepared by {narrative.preparedBy}</p>
                        )}
                    </div>
                </div>

                {hasText(narrative.monthInBrief) && (
                    <Block className="pt-10">
                        <SectionTitle>The month in brief</SectionTitle>
                        <p className="text-report-navy text-lg leading-relaxed whitespace-pre-line italic">
                            {narrative.monthInBrief}
                        </p>
                    </Block>
                )}

                {reachItems.length > 0 && (
                    <Block>
                        <SectionTitle>Reach</SectionTitle>
                        <CardRows perRow={3} gap="gap-6">
                            {reachItems.map((item) => (
                                <FigureCard key={item.id} item={item} value={metricValue(item.id, stats, lmsStats)} />
                            ))}
                        </CardRows>
                    </Block>
                )}

                {(showTiers || coachingItems.length > 0) && (
                    <Block>
                        <SectionTitle>Depth of engagement</SectionTitle>
                        {showTiers && (
                            <MeterRows
                                unit="users"
                                rows={stats.frequencyTiers.map((row) => ({
                                    label: frequencyTierLabelMap[row.tier] ?? row.tier,
                                    value: row.userCount,
                                }))}
                            />
                        )}
                        {coachingItems.length > 0 && (
                            <CardRows perRow={4} gap="gap-4" className={cn(showTiers && "mt-7")}>
                                {coachingItems.map((item) => {
                                    const colors = COACHING_TILES[item.id];
                                    return (
                                        <div key={item.id} className={cn("rounded-2xl px-4 py-4", colors?.tile)}>
                                            <p
                                                className={cn(
                                                    "font-condensed text-5xl leading-tight font-semibold",
                                                    colors?.figure,
                                                )}
                                            >
                                                {metricValue(item.id, stats, lmsStats)}
                                            </p>
                                            <p className={cn("mt-1 text-sm font-semibold", colors?.label)}>
                                                {item.label}
                                            </p>
                                        </div>
                                    );
                                })}
                            </CardRows>
                        )}
                    </Block>
                )}

                {(showTopics || showTags || emailItems.length > 0) && (
                    <Block>
                        <SectionTitle>What students are engaging with</SectionTitle>
                        <CardRows perRow={2} gap="gap-5">
                            {showTopics && (
                                <div className="bg-report-cream rounded-3xl px-5 py-4">
                                    <CardTitle>Top content this month</CardTitle>
                                    <RankedList
                                        rows={[...stats.videoViewSummaries]
                                            .slice(0, 5)
                                            .sort((a, b) => b.views - a.views)
                                            .map((video) => ({
                                                key: video.contentId,
                                                label: video.title,
                                                views: includeTopicViews ? video.views : undefined,
                                            }))}
                                    />
                                </div>
                            )}
                            {emailItems.length > 0 && (
                                <div className="bg-report-cream rounded-3xl px-5 py-4">
                                    <CardTitle>Channel breakdown</CardTitle>
                                    <CardRows perRow={2} gap="gap-x-6 gap-y-3">
                                        {emailItems.map((item) => (
                                            <div key={item.id}>
                                                <p className="text-report-navy text-lg font-bold">
                                                    {metricValue(item.id, stats, lmsStats)}
                                                </p>
                                                <p className="text-report-navy text-sm">{item.label}</p>
                                            </div>
                                        ))}
                                    </CardRows>
                                </div>
                            )}
                            {showTags && (
                                <div className="bg-report-cream rounded-3xl px-5 py-4">
                                    <CardTitle>Top video tags</CardTitle>
                                    <RankedList
                                        rows={topVideoTags(stats.videoViewSummaries).map((tag) => ({
                                            key: tag.tag,
                                            label: tag.tag,
                                            views: includeTagViews ? tag.views : undefined,
                                        }))}
                                    />
                                </div>
                            )}
                        </CardRows>
                    </Block>
                )}

                {lmsItems.length > 0 && (
                    <Block>
                        <SectionTitle>Learning management system</SectionTitle>
                        <CardRows perRow={3} gap="gap-6">
                            {lmsItems.map((item) => (
                                <FigureCard key={item.id} item={item} value={metricValue(item.id, stats, lmsStats)} />
                            ))}
                        </CardRows>
                    </Block>
                )}

                {included("engagement-buckets") && (
                    <Block>
                        <SectionTitle>Engagement by strategy</SectionTitle>
                        <p className="text-report-slate mb-5 italic">
                            Total user actions by engagement strategy, {timeFrame.label}.
                        </p>
                        {/* One column per strategy, as on the stats page. Stacked into one bar, a
                            strategy with a handful of actions was a sliver beside the largest. */}
                        <ColumnChart
                            series={[{ key: "total", label: "Actions", fill: "bg-report-teal-deep" }]}
                            rows={stats.byEngagementCategory.map((row) => ({
                                label: engagementCategoryLabelMap[row.category] ?? row.category,
                                values: { total: row.total },
                            }))}
                        />
                    </Block>
                )}

                {(showPlatformActions || showPlatformUsers) && (
                    <Block>
                        <SectionTitle>Platforms</SectionTitle>
                        <div className="flex flex-col gap-6">
                            {showPlatformActions && (
                                <div>
                                    <CardTitle>Actions by platform</CardTitle>
                                    <MeterRows
                                        unit="actions"
                                        rows={clientPlatforms(stats.byPlatform).map((row) => ({
                                            label: platformLabel(row.platform),
                                            value: row.total,
                                        }))}
                                    />
                                </div>
                            )}
                            {showPlatformUsers && (
                                <div>
                                    <CardTitle>Users by platform</CardTitle>
                                    <MeterRows
                                        unit="users"
                                        rows={clientPlatforms(stats.usersByPlatform).map((row) => ({
                                            label: platformLabel(row.platform),
                                            value: row.total,
                                        }))}
                                    />
                                </div>
                            )}
                        </div>
                    </Block>
                )}

                {included("actions-per-month") && (
                    <Block>
                        <SectionTitle>Actions per month</SectionTitle>
                        <ColumnChart
                            series={[{ key: "totalActions", label: "Total actions", fill: "bg-report-teal-deep" }]}
                            rows={stats.byMonth.map((row) => ({
                                label: formatMonthLabel(row.month),
                                values: { totalActions: row.totalActions },
                            }))}
                        />
                    </Block>
                )}

                {included("actions-per-month-by-user") && (
                    <Block>
                        <SectionTitle>Actions per month by user</SectionTitle>
                        <ColumnChart
                            series={[
                                { key: "totalActions", label: "Total actions", fill: "bg-report-teal-deep" },
                                { key: "activeUsers", label: "Active users", fill: "bg-report-navy" },
                            ]}
                            rows={stats.byMonth.map((row) => ({
                                label: formatMonthLabel(row.month),
                                values: { totalActions: row.totalActions, activeUsers: row.activeUsers },
                            }))}
                        />
                    </Block>
                )}

                {showDaily && (
                    <Block>
                        <SectionTitle>Daily actions</SectionTitle>
                        <ColumnChart
                            series={[{ key: "total", label: "Actions", fill: "bg-report-teal-deep" }]}
                            rows={dailyActions(stats.byDay, timeFrame.from).map((row) => ({
                                label: String(Number(row.day.slice(-2))),
                                values: { total: row.total },
                            }))}
                        />
                    </Block>
                )}

                {included("user-growth") && membershipStats && (
                    <Block>
                        <SectionTitle>User growth</SectionTitle>
                        <ColumnChart
                            series={[{ key: "total", label: "Total users", fill: "bg-report-teal-deep" }]}
                            rows={membershipStats.map((row) => ({
                                label: formatMonthLabel(row.month),
                                values: { total: row.total },
                            }))}
                        />
                    </Block>
                )}

                {events.length > 0 && (
                    <Block>
                        <SectionTitle>Virtual & in-person engagement</SectionTitle>
                        <CardRows perRow={2} gap="gap-2">
                            {events.map((card) => (
                                <EventCard key={card.title} {...card} />
                            ))}
                        </CardRows>
                    </Block>
                )}

                {isTestimonialWritten(narrative.testimonial) && (
                    <Block>
                        <SectionTitle>Student stories & testimonials</SectionTitle>
                        <figure className="bg-report-cream rounded-3xl px-6 py-7">
                            <blockquote className="text-report-navy whitespace-pre-line italic">
                                “{narrative.testimonial.quote}”
                            </blockquote>
                            {hasText(narrative.testimonial.attribution) && (
                                <figcaption className="text-report-slate mt-2 text-right text-sm">
                                    — {narrative.testimonial.attribution}
                                </figcaption>
                            )}
                        </figure>
                    </Block>
                )}

                {audiences.map((audience, index) => (
                    <Block key={index}>
                        {index === 0 && <SectionTitle>What this means for you</SectionTitle>}
                        <div className="bg-report-cream rounded-3xl px-6 py-6">
                            {hasText(audience.title) && (
                                <h3 className="font-condensed text-report-navy mb-2 text-lg font-bold tracking-wide uppercase">
                                    {audience.title}
                                </h3>
                            )}
                            {hasText(audience.body) && (
                                <p className="text-report-navy whitespace-pre-line">{audience.body}</p>
                            )}
                            {hasText(audience.nextStep) && (
                                <p className="text-report-navy mt-3 whitespace-pre-line">
                                    <span className="text-report-teal-deep font-semibold">Recommended next step:</span>{" "}
                                    {audience.nextStep}
                                </p>
                            )}
                        </div>
                    </Block>
                ))}
            </div>
        );
    },
);
PartnerReport.displayName = "PartnerReport";
